import React from 'react';
import { T } from '../../../theme/tokens';
import type { GridConfig, KpiDef } from '../DataGrid';
import type { PunchRow } from '../../../data/useParcels';
import { Pill, LotCell, DocsCell, fmtDate, parseDate, dash } from '../cells';
import { isoDayOf } from '../../../util/date';
import { facetText, facetFlag, facetPerson } from '../filterModel';
import { pctOf } from '../perspectives';

// ---- Lifecycle (Status) — the five live singleSelect options, in workflow order. These PARTITION
// the rows: every punch item is in exactly one, so the lifecycle KPIs sum to the total (tested).
export const PUNCH_STATUSES = ['Open', 'Sent to Sub', 'Fixed - Verify', 'Reopened', 'Closed'] as const;
const norm = (s: string) => (s || '').trim().toLowerCase();
/** Lifecycle bucket — folds any drift to one of the five canonical statuses; an unknown/blank
 *  status lands in 'Open' so the partition still sums to N (no item silently disappears). */
export function statusBucket(s: string): typeof PUNCH_STATUSES[number] {
  const v = norm(s);
  if (v.includes('closed')) return 'Closed';
  if (v.includes('reopen')) return 'Reopened';
  if (v.includes('fixed') || v.includes('verify')) return 'Fixed - Verify';
  if (v.includes('sent')) return 'Sent to Sub';
  return 'Open';
}
const statusColor = (s: string): string => {
  switch (statusBucket(s)) {
    case 'Closed': return T.accent;
    case 'Fixed - Verify': return '#6fae7e';
    case 'Sent to Sub': return T.gold;
    case 'Reopened': return T.maroon;
    default: return T.amber; // Open
  }
};

// ---- Aging (formula text): "Closed" / "0-7 days" / "8-14 days" / "15+ days OVERDUE" ----
export function agingColor(aging: string): string {
  const a = norm(aging);
  if (a.includes('overdue') || a.includes('15+')) return T.maroon;
  if (a.includes('8-14')) return T.gold;
  if (a.includes('closed')) return T.accent;
  return T.inkSoft; // "0-7 days" / unknown → neutral
}
// Month bucket on the item's activity date (Sent, falling back to Closed). ISO-prefixed so the
// keys sort chronologically via sortByKey (same pattern as the inspections day groups); the long
// month name rides after the prefix for readability — e.g. "2026-05 · May".
const monthKey = (r: PunchRow): string => {
  const iso = isoDayOf(r.sentDate || r.closedDate);
  if (!iso) return '~ no date';
  const ym = iso.slice(0, 7);
  return `${ym} · ${new Date(ym + '-01T12:00:00').toLocaleDateString('en-US', { month: 'long' })}`;
};
const isOverdue = (r: PunchRow) => /overdue|15\+/.test(norm(r.aging));
const isOpen = (r: PunchRow) => statusBucket(r.status) === 'Open';
const isHigh = (r: PunchRow) => norm(r.priority) === 'high';
// Active work = anything not yet closed (the three open-ish lifecycle states + reopened).
const isActive = (r: PunchRow) => statusBucket(r.status) !== 'Closed';

/** Overview takeaways — pure, computed from the scoped rows (never hardcoded). Exported for the
 *  regression harness. "<N> open across <M> lots — <K> overdue 15+ days, <H> high priority; <C>% closed". */
export function punchTakeaways(rows: PunchRow[]): string[] {
  if (!rows.length) return [];
  const n = (x: number) => x.toLocaleString('en-US');
  const openRows = rows.filter(isOpen);
  const open = openRows.length;
  // "N open across M lots" — M = the lots that actually carry an open item (not the whole span).
  const lots = new Set(openRows.map(r => r.lotId).filter(Boolean)).size;
  const overdue = rows.filter(isOverdue).length;
  const high = rows.filter(isHigh).length;
  const closed = rows.filter(r => statusBucket(r.status) === 'Closed').length;
  const out: string[] = [];
  out.push(`${n(open)} open across ${n(lots)} lot${lots === 1 ? '' : 's'} — ${n(overdue)} overdue 15+ days, ${n(high)} high priority; ${pctOf(closed, rows.length)}% closed.`);
  // (Trade-mix line removed 2026-06-28 — the "Trade (from Company)" lookup was a broken/empty
  //  field and was deleted in the data-room cleanup; re-add if a working trade lookup is created.)
  return out;
}

export function makePunchConfig(): GridConfig<PunchRow> {
  // ---- KPIs (TWO-AXIS) -----------------------------------------------------------------------
  // Lifecycle cluster: PARTITIONS the rows (Open + Sent to Sub + Fixed-Verify + Reopened + Closed
  // = total). 'lifecycle' axis = plain (summable). Attention cluster: overlapping worklists, tinted.
  const lifecycleKpi = (label: typeof PUNCH_STATUSES[number], tone?: 'accent' | 'amber'): KpiDef<PunchRow> => ({
    label, cluster: 'Lifecycle', axis: 'lifecycle',
    define: `Punch items whose Status is “${label}”.`,
    compute: rows => ({ display: '', n: rows.filter(r => statusBucket(r.status) === label).length, tone }),
    filter: r => statusBucket(r.status) === label,
  });
  const kpis: KpiDef<PunchRow>[] = [
    { label: 'Punch items', define: 'All punch items for the lots in view, inside the active search.',
      compute: rows => ({ display: '', n: rows.length }) },
    { ...lifecycleKpi('Open', 'amber'), define: 'Punch items still Open — not yet sent, fixed, or closed.' },
    lifecycleKpi('Sent to Sub'),
    lifecycleKpi('Fixed - Verify'),
    lifecycleKpi('Reopened'),
    lifecycleKpi('Closed', 'accent'),
    // Attention cluster — overlapping worklists (do NOT sum).
    { label: 'Overdue', cluster: 'Attention', axis: 'attention',
      define: 'Punch items aging 15+ days (Aging = “15+ days OVERDUE”).',
      compute: rows => ({ display: '', n: rows.filter(isOverdue).length, tone: 'maroon' }), filter: isOverdue },
    { label: 'High priority', cluster: 'Attention', axis: 'attention',
      define: 'Punch items flagged High priority.',
      compute: rows => ({ display: '', n: rows.filter(isHigh).length, tone: 'maroon' }), filter: isHigh },
    { label: 'Still open', cluster: 'Attention', axis: 'attention',
      define: 'Punch items whose Status is Open (the unworked backlog).',
      compute: rows => ({ display: '', n: rows.filter(isOpen).length, tone: 'amber' }), filter: isOpen },
    { label: 'Active', cluster: 'Attention', axis: 'attention',
      define: 'Punch items not yet Closed — every open, sent, fixed-verify, or reopened item.',
      compute: rows => ({ display: '', n: rows.filter(isActive).length, tone: 'amber' }), filter: isActive },
  ];

  // ---- Columns -------------------------------------------------------------------------------
  const columns: GridConfig<PunchRow>['columns'] = [
    { key: 'punch', header: 'Punch ID', width: 230, sortable: true, section: 'Identity', value: r => r.punchId,
      render: r => <span title={r.punchId} style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block' }}>{dash(r.punchId)}</span> },
    // Lot identifier — block-lot + the unit it belongs to; click selects the lot (resolved Unit → Lot).
    { key: 'lot', header: 'Lot', width: 180, sortable: true, section: 'Identity', value: r => r.blockLot || r.unitName,
      render: (r, ctx) => <LotCell blockLot={r.blockLot} lotId={r.lotId} name={r.unitName} extra={Math.max(0, r.lotIds.length - 1)} ctx={ctx} /> },
    { key: 'model', header: 'Model', width: 110, sortable: true, section: 'Identity', value: r => r.model, render: r => <span>{dash(r.model)}</span> },
    { key: 'issue', header: 'Issue', width: 260, sortable: true, section: 'Identity', value: r => r.issue,
      render: r => r.issue
        ? <span title={r.issue} style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block' }}>{r.issue}</span>
        : <span style={{ color: T.inkSoft }}>—</span> },
    { key: 'status', header: 'Status', width: 130, sortable: true, section: 'Status', value: r => statusBucket(r.status),
      render: r => <Pill label={statusBucket(r.status)} color={statusColor(r.status)} /> },
    { key: 'priority', header: 'Priority', width: 92, sortable: true, section: 'Status', value: r => r.priority,
      render: r => r.priority ? <Pill label={r.priority} color={isHigh(r) ? T.maroon : T.inkSoft} /> : <span style={{ color: T.inkSoft }}>—</span> },
    // Aging — formula text, colored by band; sort by Days Open so the order is true age order.
    { key: 'aging', header: 'Aging', width: 150, sortable: true, defaultDesc: true, section: 'Status', value: r => r.aging,
      sortValue: r => r.daysOpen,
      render: r => r.aging ? <Pill label={r.aging} color={agingColor(r.aging)} /> : <span style={{ color: T.inkSoft }}>—</span> },
    { key: 'days', header: 'Days open', width: 96, align: 'right', sortable: true, defaultDesc: true, section: 'Status', value: r => r.daysOpen,
      render: r => r.daysOpen == null ? <span style={{ color: T.inkSoft }}>—</span> : <span className="num">{r.daysOpen}</span> },
    { key: 'company', header: 'Company', width: 150, sortable: true, section: 'Assignment', value: r => r.company,
      render: r => <span title={r.company || undefined} style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block' }}>{dash(r.company)}</span> },
    { key: 'reportedby', header: 'Reported by', width: 140, sortable: true, hideWhenEmpty: true, section: 'Assignment', value: r => r.reportedBy,
      render: r => <span title={r.reportedBy || undefined} style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block' }}>{dash(r.reportedBy)}</span> },
    { key: 'sent', header: 'Sent', width: 92, align: 'right', sortable: true, defaultDesc: true, hideWhenEmpty: true, section: 'Activity',
      value: r => r.sentDate, sortValue: r => parseDate(r.sentDate) || null,
      render: r => <span className="num" title={(r.sentDate || '').slice(0, 10)} style={{ color: T.inkSoft, fontSize: 11.5 }}>{fmtDate(r.sentDate)}</span> },
    { key: 'closed', header: 'Closed', width: 92, align: 'right', sortable: true, defaultDesc: true, hideWhenEmpty: true, section: 'Activity',
      value: r => r.closedDate, sortValue: r => parseDate(r.closedDate) || null,
      render: r => <span className="num" title={(r.closedDate || '').slice(0, 10)} style={{ color: T.inkSoft, fontSize: 11.5 }}>{fmtDate(r.closedDate)}</span> },
    { key: 'photos', header: 'Photos', width: 104, hideWhenEmpty: true, sortable: true, section: 'Documents',
      value: r => r.photos.map(f => f.filename).join('; '),
      render: (r, ctx) => <DocsCell docs={r.photos} ctx={ctx} /> },
  ];

  return {
    columns, kpis,
    excel: {
      sheetTitle: 'Punch List',
      date: ['sent', 'closed'],                                  // real Excel dates
      tone: {
        status: r => { const b = statusBucket(r.status);
          return b === 'Closed' ? 'good' : b === 'Reopened' ? 'blocked'
            : b === 'Open' ? 'blocked' : 'progress'; },
        priority: r => isHigh(r) ? 'blocked' : 'neutral',
        aging: r => { const a = norm(r.aging);
          return a.includes('overdue') || a.includes('15+') ? 'blocked'
            : a.includes('8-14') ? 'progress' : a.includes('closed') ? 'good' : 'neutral'; },
      },
      pdfColumns: ['punch', 'lot', 'issue', 'status', 'priority', 'aging', 'company'],
      pdfHeaders: { lot: 'Lot · Unit' },
    },
    groupKey: r => statusBucket(r.status),
    dateOf: r => r.sentDate || r.closedDate,
    groupOrder: [...PUNCH_STATUSES],
    defaultSort: { key: 'aging', dir: 'desc' },  // oldest / most overdue first
    rowLotId: r => r.lotId,
    rowKey: r => r.id,
    ariaLabel: r => `Punch ${r.punchId || '—'}, lot ${r.blockLot || '—'}, ${statusBucket(r.status)}${isHigh(r) ? ', high priority' : ''}${isOverdue(r) ? ', overdue' : ''}`,
    groupOptions: [
      { key: 'status', label: 'Status', keyOf: r => statusBucket(r.status), col: 'status' },
      { key: 'priority', label: 'Priority', keyOf: r => r.priority || 'Normal', col: 'priority' },
      { key: 'aging', label: 'Aging', keyOf: r => r.aging || '—', col: 'aging' },
      { key: 'company', label: 'Company', keyOf: r => r.company || '—' },
      { key: 'lot', label: 'Lot', keyOf: r => r.blockLot ? `${r.blockLot}${r.unitName ? ` · ${r.unitName}` : ''}` : (r.unitName || '—'), col: 'lot' },
      { key: 'phase', label: 'Master Phase', keyOf: r => r.masterPhase || '—' },
      { key: 'month', label: 'Month (sent / closed)', keyOf: monthKey, sortByKey: true },
    ],
    groupStats: rows => {
      const n = rows.length || 1;
      const closed = rows.filter(r => statusBucket(r.status) === 'Closed').length;
      const pctClosed = Math.round((closed / n) * 100);
      const overdue = rows.filter(isOverdue).length;
      const high = rows.filter(isHigh).length;
      const chip = (l: string, c: string) => <span className="num" style={{ fontSize: 11, color: c, fontWeight: 600 }}>{l}</span>;
      return <>{chip(`${pctClosed}% closed`, T.inkSoft)}{overdue > 0 && chip(`${overdue} overdue`, T.maroon)}{high > 0 && chip(`${high} high`, T.maroon)}</>;
    },
    // field:value facets for the quick-filter input (documented in its title): status:, priority:,
    // aging:, trade:, lot:, reportedby:. e.g. status:open · aging:overdue · priority:high.
    facets: {
      status: facetText(r => statusBucket(r.status)),
      priority: facetText(r => r.priority),
      aging: facetText(r => r.aging),
      company: facetText(r => r.company),
      lot: facetText(r => `${r.blockLot} ${r.unitName} ${r.lotName}`),
      reportedby: facetPerson(r => r.reportedBy),
      photo: facetFlag(r => r.photos.length > 0),
    },
    presetViews: [
      { name: 'Open work', preset: true, sort: { key: 'aging', dir: 'desc' }, groupBy: 'status' },
      { name: 'Overdue first', preset: true, chips: ['Overdue'], sort: { key: 'days', dir: 'desc' } },
      { name: 'High priority', preset: true, chips: ['High priority'] },
    ],
    // Curated sub-views (pill row under the tab bar; "All" = this full config).
    perspectives: [
      { key: 'open', label: 'Open work',
        blurb: 'Everything still on the punch list — open, sent to sub, or reopened, oldest first.',
        kpiKeys: ['Open', 'Sent to Sub', 'Reopened', 'Overdue', 'High priority'],
        columnKeys: ['punch', 'lot', 'issue', 'status', 'priority', 'aging', 'days', 'company', 'photos'],
        // Active-only via the "Active" chip (Closed never appears); grouped by status, oldest first.
        // (Same-key facets AND together, so a status:open/status:sent search would match nothing —
        // a single "not closed" chip is the honest way to show all three open-ish states.)
        defaults: { chips: ['Active'], sortKey: 'aging', sortDir: 'desc', groupBy: 'status' } },
      { key: 'attention', label: 'Overdue & high priority',
        blurb: 'The items that need a push — 15+ days overdue and high priority, most aged first.',
        kpiKeys: ['Overdue', 'High priority', 'Still open'],
        columnKeys: ['punch', 'lot', 'issue', 'status', 'priority', 'aging', 'days', 'reportedby'],
        defaults: { chips: ['Overdue', 'High priority'], sortKey: 'days', sortDir: 'desc' } },
      { key: 'overview', label: 'Overview',
        blurb: 'The big picture — lifecycle partition, attention overlays, and where the work sits.',
        kpiKeys: ['Punch items', 'Open', 'Sent to Sub', 'Fixed - Verify', 'Reopened', 'Closed'],
        columnKeys: ['punch', 'lot', 'issue', 'status', 'priority', 'aging', 'company'],
        defaults: { groupBy: 'status' },
        takeaways: punchTakeaways },
    ],
    empty: 'No punch items for this selection.',
  };
}
